import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { AbstractControl, ValidationErrors } from '@angular/forms';

@Component({
  selector: 'app-password-strength',
  standalone: true,
  imports: [CommonModule, TranslocoModule],
  templateUrl: './password-strength.component.html',
  styleUrls: ['./password-strength.component.scss'],
})
export class PasswordStrengthComponent implements OnChanges {
  @Input() password: string = '';

  // Password strength properties
  passwordStrength = 0;
  passwordStrengthText = '';
  passwordStrengthClass = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['password']) {
      this.checkPasswordStrength(this.password);
    }
  }

  /**
   * Evaluates password strength and updates strength indicators
   */
  checkPasswordStrength(password: string) {
    // Reset strength indicator
    this.passwordStrength = 0;

    if (!password) {
      this.passwordStrengthText = '';
      this.passwordStrengthClass = '';
      return;
    }

    // Check for minimum length
    if (password.length >= 6) {
      this.passwordStrength++;
    }

    // Check for uppercase letters
    if (/[A-Z]/.test(password)) {
      this.passwordStrength++;
    }

    // Check for lowercase letters
    if (/[a-z]/.test(password)) {
      this.passwordStrength++;
    }

    // Check for numbers
    if (/[0-9]/.test(password)) {
      this.passwordStrength++;
    }

    // Update strength text and class based on score
    switch (this.passwordStrength) {
      case 0:
      case 1:
      case 2:
        this.passwordStrengthText = 'auth.register.passwordStrength.weak';
        this.passwordStrengthClass = 'weak';
        break;
      case 3:
        this.passwordStrengthText = 'auth.register.passwordStrength.medium';
        this.passwordStrengthClass = 'medium';
        break;
      case 4:
        this.passwordStrengthText = 'auth.register.passwordStrength.strong';
        this.passwordStrengthClass = 'strong';
        break;
      default:
        this.passwordStrengthText = '';
        this.passwordStrengthClass = '';
    }
  }

  /**
   * Static validator function for use in form controls
   */
  static passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.value;
    if (!password) {
      return null;
    }

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /[0-9]/.test(password);
    const hasMinLength = password.length >= 6;

    const passwordValid = hasUpperCase && hasLowerCase && hasNumbers && hasMinLength;

    return passwordValid ? null : { weakPassword: true };
  }
}
